<template>
  <!-- 사진 받는 곳·기한 안내(결제 완료·마이페이지 예약 상세가 같이 쓴다). 글자는 받은 그대로, 모양만 정한다 -->
  <section class="photo-guide">
    <p class="photo-guide__lead"><KeepTogether :text="lead" chip /></p>
    <ul class="photo-guide__rows">
      <li v-for="row in rows" :key="row.kind" class="photo-guide__row">
        <span class="photo-guide__icon" aria-hidden="true">
          <svg v-if="row.kind === 'original'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><circle cx="9" cy="10" r="1.6" /><path d="m21 16-5-5-8 8" /></svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 17h16M5 14.5 3.5 7l5 3.5L12 5l3.5 5.5 5-3.5L19 14.5" /></svg>
        </span>
        <span class="photo-guide__text">
          <strong class="photo-guide__label">{{ row.label }}</strong>
          <span class="photo-guide__body"><KeepTogether :text="row.body" /></span>
          <span v-if="row.aside" class="photo-guide__aside">{{ row.aside }}</span>
        </span>
      </li>
    </ul>
    <p v-if="note" class="photo-guide__note">{{ note }}</p>
  </section>
</template>

<script>
import KeepTogether from "@/views/components/KeepTogether"

// "1. 원본: 등록일로부터 14일 동안 다운로드 가능 (14일이 지나면 …)"을
// 이름('1. 원본:') / 기한 본문(굵게) / 맨 끝 괄호 덧말(옅게)로 나눈다. 글자는 그대로, 쌍점(：도)이 없으면 통째로 본문
function splitRow(text) {
  const at = text.search(/[:：]/)
  const label = at < 0 ? "" : text.slice(0, at + 1)
  let body = at < 0 ? text : text.slice(at + 1).trim()
  let aside = ""
  // 덧말 괄호 앞 빈칸은 한·영에만 있다(일본어는 붙여 쓴다)
  const open = body.search(/(?:\s[(（]|（)[^()（）]*[)）]$/)
  if (open > 0) {
    aside = body.slice(open).trim()
    body = body.slice(0, open).trim()
  }
  return { label, body, aside }
}

export default {
  name: "PhotoDeliveryGuide",
  components: { KeepTogether },
  props: {
    lead: { type: String, required: true },
    original: { type: String, required: true },
    completed: { type: String, required: true },
    note: { type: String, default: "" },
  },
  computed: {
    rows() {
      return [
        { kind: "original", ...splitRow(this.original) },
        { kind: "completed", ...splitRow(this.completed) },
      ]
    },
  },
}
</script>

<style scoped>
/* 브랜드 색: 종이 #fffdf9, 금색 선 rgba(190,160,104), 글자 #5c2e2e(종이 위 대비 11:1), 분홍 #b9345d(6.0:1) */
.photo-guide {
  padding: 18px 18px 16px;
  border: 1px solid rgba(190, 160, 104, 0.38);
  border-radius: 16px;
  background: #fffdf9;
  color: #5c2e2e;
  text-align: left;
  word-break: keep-all;
  overflow-wrap: break-word;
}

/* 일본어는 낱말 사이 빈칸이 없어 keep-all이면 줄을 못 바꿔 칸 밖으로 나간다(10/17 390 폭 괄호 덧말). 일본어 줄바꿈 규칙대로 */
:lang(ja) .photo-guide {
  word-break: normal;
  word-break: auto-phrase; /* 되는 브라우저에선 가타카나 낱말을 안 가른다 */
  line-break: strict;
  text-wrap: pretty;
  text-spacing-trim: trim-start;
}

:lang(ja) .photo-guide__aside {
  text-wrap: balance;
}

.photo-guide__lead {
  margin: 0;
  font-size: 15px;
  line-height: 1.7;
}

.photo-guide__rows {
  display: grid;
  gap: 10px;
  margin: 14px 0 0;
  padding: 14px 0 0;
  border-top: 1px solid rgba(190, 160, 104, 0.28);
  list-style: none;
}

.photo-guide__row {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  align-items: start;
}

.photo-guide__icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fdf0f0;
  color: #b9345d;
}

.photo-guide__icon svg {
  width: 19px;
  height: 19px;
}

.photo-guide__text {
  display: block;
  padding-top: 1px;
  font-size: 14px;
  line-height: 1.6;
}

.photo-guide__label {
  display: block;
  color: #b9345d;
  font-size: 14px;
  font-weight: 700;
}

.photo-guide__body {
  display: block;
  color: #5c2e2e;
  font-weight: 600;
}

.photo-guide__aside {
  display: block;
  color: #7a5656;
  font-size: 13px;
}

/* 좁은 휴대폰: 아이콘·여백을 줄여 본문 칸을 넓히고 창이 길어지지 않게 */
@media (max-width: 420px) {
  .photo-guide {
    padding: 16px 14px 14px;
  }
  .photo-guide__lead {
    line-height: 1.6;
  }
  .photo-guide__row {
    grid-template-columns: 28px 1fr;
    gap: 10px;
  }
  .photo-guide__icon {
    width: 28px;
    height: 28px;
  }
  .photo-guide__icon svg {
    width: 16px;
    height: 16px;
  }
}

.photo-guide__note {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fef6ee;
  color: #7a5656;
  font-size: 13px;
  line-height: 1.6;
}
</style>
